import React from "react";
import { withRouter } from "react-router-dom";
import { CircularProgress } from "material-ui";
import * as SearchService from "../../services/search-service";
import * as PaperStates from "../../general/paper-state";
import { highlight } from "../../general/search-highlight";
import { DS } from "../../theme-default";

const TYPEAHEAD_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const TYPEAHEAD_GROUP_SIZE = 5;

const stateColor = (state) => {
  if (PaperStates.isApproved(state)) return DS.success;
  if (PaperStates.isRejected(state)) return DS.error;
  if (PaperStates.isDraft(state)) return DS.textMuted;
  return DS.warning;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return (
    date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear()
  );
};

class GlobalSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      open: false,
      loading: false,
      results: null,
      error: false,
      selectedIndex: -1,
    };
    this.requestSeq = 0;
    this.containerRef = React.createRef();
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this._onDocumentMouseDown);
    document.addEventListener("keydown", this._onDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this._onDocumentMouseDown);
    document.removeEventListener("keydown", this._onDocumentKeyDown);
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  _onDocumentMouseDown = (e) => {
    if (
      this.containerRef.current &&
      !this.containerRef.current.contains(e.target)
    ) {
      this.setState({ open: false, selectedIndex: -1 });
    }
  };

  _onDocumentKeyDown = (e) => {
    const isShortcut =
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") ||
      (e.key === "/" &&
        !["INPUT", "TEXTAREA"].includes((e.target.tagName || "").toUpperCase()) &&
        !e.target.isContentEditable);
    if (isShortcut) {
      e.preventDefault();
      if (this.inputRef.current) {
        this.inputRef.current.focus();
        this.inputRef.current.select();
      }
    }
  };

  _onInputChange = (e) => {
    const query = e.target.value;
    this.setState({ query, selectedIndex: -1 });
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (query.trim().length < MIN_QUERY_LENGTH) {
      this.requestSeq++; // invalidate any in-flight request
      this.setState({ open: false, results: null, loading: false, error: false });
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this._runSearch(query);
    }, TYPEAHEAD_DEBOUNCE_MS);
  };

  _runSearch = (query) => {
    const seq = ++this.requestSeq;
    this.setState({ loading: true, open: true, error: false });
    SearchService.globalSearch(query, TYPEAHEAD_GROUP_SIZE)
      .then((response) => {
        if (seq !== this.requestSeq) {
          return; // stale response, a newer query is in flight
        }
        this.setState({
          results: response.data,
          loading: false,
          open: true,
        });
      })
      .catch(() => {
        if (seq !== this.requestSeq) {
          return;
        }
        this.setState({ loading: false, results: null, error: true });
      });
  };

  _navigableItems = () => {
    const { results, loading } = this.state;
    if (!results || loading) {
      // while loading, the dropdown shows only the spinner — the previous
      // query's items are invisible, so they must not be keyboard-reachable
      return [];
    }
    const items = [];
    const papers = (results.papers && results.papers.items) || [];
    papers.forEach((paper) => items.push({ type: "paper", data: paper }));
    const contracts = (results.contracts && results.contracts.items) || [];
    contracts.forEach((contract) =>
      items.push({ type: "contract", data: contract }),
    );
    if (this._totalCount() > 0) {
      items.push({ type: "all" });
    }
    return items;
  };

  _totalCount = () => {
    const { results } = this.state;
    if (!results) {
      return 0;
    }
    const papersTotal = results.papers ? results.papers.total : 0;
    const contractsTotal = results.contracts ? results.contracts.total : 0;
    return papersTotal + contractsTotal;
  };

  _onInputKeyDown = (e) => {
    const items = this._navigableItems();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({
        open: true,
        selectedIndex: Math.min(this.state.selectedIndex + 1, items.length - 1),
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({
        selectedIndex: Math.max(this.state.selectedIndex - 1, -1),
      });
    } else if (e.key === "Enter") {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      const selected = items[this.state.selectedIndex];
      if (selected) {
        this._openItem(selected);
      } else if (this.state.query.trim().length >= MIN_QUERY_LENGTH) {
        this._goToResultsPage();
      }
    } else if (e.key === "Escape") {
      this.setState({ open: false, selectedIndex: -1 });
      if (this.inputRef.current) {
        this.inputRef.current.blur();
      }
    }
  };

  _onFocus = () => {
    if (
      this.state.results &&
      this.state.query.trim().length >= MIN_QUERY_LENGTH
    ) {
      this.setState({ open: true });
    }
  };

  _close = () => {
    this.setState({ open: false, selectedIndex: -1 });
  };

  _openItem = (item) => {
    if (item.type === "paper") {
      this._close();
      this.props.history.push(
        "/app/referate/0?filter=all&q=" + encodeURIComponent(item.data.code),
      );
    } else if (item.type === "contract") {
      const year = item.data.contractDate
        ? new Date(item.data.contractDate).getFullYear()
        : new Date().getFullYear();
      this._close();
      this.props.history.push(
        "/app/contracts?year=" +
          year +
          "&q=" +
          encodeURIComponent(item.data.contractNr || ""),
      );
    } else if (item.type === "all") {
      this._goToResultsPage();
    }
  };

  _goToResultsPage = () => {
    this._close();
    this.props.history.push(
      "/app/search?q=" + encodeURIComponent(this.state.query.trim()),
    );
  };

  _renderGroupHeader = (label, total) => (
    <div
      style={{
        padding: "8px 14px 4px 14px",
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: DS.textMuted,
      }}
    >
      {label}
      {total > 0 && (
        <span style={{ float: "right", fontWeight: 400 }}>{total}</span>
      )}
    </div>
  );

  _itemStyle = (isSelected) => ({
    padding: "10px 16px",
    cursor: "pointer",
    backgroundColor: isSelected ? DS.bg : DS.surface,
    borderLeft: isSelected
      ? "3px solid " + DS.primary
      : "3px solid transparent",
  });

  _renderPaperItem = (paper, index) => {
    const isSelected = index === this.state.selectedIndex;
    const query = this.state.query;
    return (
      <div
        key={"paper-" + paper.id}
        style={this._itemStyle(isSelected)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => this._openItem({ type: "paper", data: paper })}
        onMouseEnter={() => this.setState({ selectedIndex: index })}
      >
        <div
          style={{
            fontSize: "15px",
            color: DS.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 600, marginRight: 8 }}>
            {highlight(paper.code, query)}
          </span>
          {highlight(paper.snippet, query)}
        </div>
        <div
          style={{
            fontSize: "13px",
            color: DS.textSecondary,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: stateColor(paper.state), fontWeight: 600 }}>
            {PaperStates.getTypeForId(paper.state)}
          </span>
          {paper.department && <span> · {highlight(paper.department, query)}</span>}
          {paper.createdBy && <span> · {highlight(paper.createdBy, query)}</span>}
          {paper.createdDate && <span> · {formatDate(paper.createdDate)}</span>}
        </div>
      </div>
    );
  };

  _renderContractItem = (contract, index) => {
    const isSelected = index === this.state.selectedIndex;
    const query = this.state.query;
    return (
      <div
        key={"contract-" + contract.id}
        style={this._itemStyle(isSelected)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => this._openItem({ type: "contract", data: contract })}
        onMouseEnter={() => this.setState({ selectedIndex: index })}
      >
        <div
          style={{
            fontSize: "15px",
            color: DS.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 600, marginRight: 8 }}>
            {highlight("Nr. " + (contract.contractNr || "-"), query)}
          </span>
          {highlight(contract.contractObject || contract.serviceName, query)}
        </div>
        <div
          style={{
            fontSize: "13px",
            color: DS.textSecondary,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {contract.supplier && (
            <span>{highlight(contract.supplier, query)}</span>
          )}
          {contract.contractDate && (
            <span> · {formatDate(contract.contractDate)}</span>
          )}
        </div>
      </div>
    );
  };

  _renderDropdown = () => {
    const { results, loading } = this.state;
    const items = this._navigableItems();
    const papers = (results && results.papers && results.papers.items) || [];
    const contracts =
      (results && results.contracts && results.contracts.items) || [];
    const contractsVisible =
      results && results.contracts !== null && results.contracts !== undefined;
    const totalCount = this._totalCount();

    let cursor = 0;

    return (
      <div className="ds-global-search-dropdown">
        {loading && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={24} thickness={2.5} />
          </div>
        )}

        {!loading && this.state.error && (
          <div
            style={{
              padding: "18px 14px",
              color: DS.error,
              fontSize: "15px",
              textAlign: "center",
            }}
          >
            Cautarea a esuat. Va rog reincercati.
          </div>
        )}

        {!loading && results && totalCount === 0 && (
          <div
            style={{
              padding: "18px 14px",
              color: DS.textSecondary,
              fontSize: "15px",
              textAlign: "center",
            }}
          >
            Niciun rezultat pentru „{this.state.query.trim()}”
          </div>
        )}

        {!loading && papers.length > 0 && (
          <div>
            {this._renderGroupHeader(
              "Referate",
              results.papers ? results.papers.total : 0,
            )}
            {papers.map((paper) => this._renderPaperItem(paper, cursor++))}
          </div>
        )}

        {!loading && contractsVisible && contracts.length > 0 && (
          <div>
            {this._renderGroupHeader(
              "Contracte",
              results.contracts ? results.contracts.total : 0,
            )}
            {contracts.map((contract) =>
              this._renderContractItem(contract, cursor++),
            )}
          </div>
        )}

        {!loading && totalCount > 0 && (
          <div
            style={{
              ...this._itemStyle(cursor === this.state.selectedIndex),
              borderTop: "1px solid " + DS.borderFaint,
              color: DS.primary,
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "center",
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => this._openItem({ type: "all" })}
            onMouseEnter={() => this.setState({ selectedIndex: items.length - 1 })}
          >
            Vezi toate rezultatele ({totalCount})
          </div>
        )}
      </div>
    );
  };

  render() {
    const { open } = this.state;
    // Positioning and responsive behavior live in design-system.css
    // (.ds-global-search*), including the phone layout where the input
    // takes the whole app bar.
    return (
      <div ref={this.containerRef} className="ds-global-search">
        <input
          ref={this.inputRef}
          className="ds-global-search-input"
          type="search"
          name="app-global-search"
          autoComplete="off"
          value={this.state.query}
          onChange={this._onInputChange}
          onKeyDown={this._onInputKeyDown}
          onFocus={this._onFocus}
          placeholder="Cauta referat, articol, compartiment...  (Ctrl+K)"
        />
        {open && this._renderDropdown()}
      </div>
    );
  }
}

// The contracts group is omitted server-side for users without the
// canSeeContracts permission, so no client-side gating is needed here.
export default withRouter(GlobalSearch);
